<template>
  <teleport to="body">
    <transition name="modal">
      <div v-if="show" class="fixed inset-0 z-[9999] overflow-y-auto">
        <!-- Backdrop -->
        <div class="modal-backdrop" @click="closeModal"></div>

        <!-- Modal -->
        <div class="modal-container">
          <div class="modal-content max-w-3xl max-h-[90vh] sm:max-h-[85vh]" @click.stop>
            <!-- Header -->
            <div class="modal-header">
              <div>
                <h2 class="modal-title">Select Buyer</h2>
                <p class="text-base text-gray-500 mt-1">Choose a company from the list below</p>
              </div>
              <button @click="closeModal" class="btn-icon btn-secondary">
                <font-awesome-icon icon="fa-solid fa-times" class="text-lg" />
              </button>
            </div>

            <!-- Tabs -->
            <div class="modal-tabs">
              <button
                v-for="tab in tabs"
                :key="tab.id"
                @click="activeTab = tab.id"
                class="modal-tab"
                :class="{ 'active': activeTab === tab.id }"
              >
                {{ tab.label }}
              </button>
            </div>

            <!-- Content -->
            <div class="modal-body">
              <!-- Companies Tab -->
              <div v-if="activeTab === 'companies'">
                <!-- Search and Sort -->
                <div class="modal-search-container flex flex-col sm:flex-row gap-4">
                  <div class="modal-search-input-wrapper flex-1">
                    <input
                      v-model="companySearch"
                      type="text"
                      placeholder="Search companies..."
                      class="form-input pl-12"
                      @input="filterCompanies"
                    />
                  </div>
                  <div class="sm:w-56">
                    <select v-model="companyOrderBy" @change="filterCompanies" class="form-input appearance-none cursor-pointer">
                      <option value="name">Alphabetically</option>
                      <option value="created_at">Recently Added</option>
                      <option value="vat_number">VAT Number</option>
                    </select>
                  </div>
                </div>

                <div v-if="loadingCompanies" class="flex items-center justify-center py-16">
                  <div class="animate-spin rounded-full h-10 w-10 border-2 border-gray-200 border-t-blue-600"></div>
                </div>

                <div v-else-if="filteredCompanies.length === 0" class="flex flex-col items-center justify-center py-16 text-center">
                  <div class="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                    <font-awesome-icon icon="fa-solid fa-building" class="text-gray-400 text-2xl" />
                  </div>
                  <p class="text-gray-900 font-medium text-lg">No companies found</p>
                  <p class="text-base text-gray-500 mt-1">Try adjusting your search terms</p>
                </div>

                <div v-else class="space-y-3">
                  <button
                    v-for="company in filteredCompanies"
                    :key="company.id"
                    @click="selectCompany(company)"
                    class="modal-list-item"
                  >
                    <div class="modal-list-item-icon company">
                      <font-awesome-icon icon="fa-solid fa-building" />
                    </div>
                    <div class="modal-list-item-content">
                      <div class="modal-list-item-header">
                        <h3 class="modal-list-item-title">{{ company.name }}</h3>
                        <span v-if="company.vat_number" class="modal-list-item-badge">
                          {{ company.vat_number }}
                        </span>
                      </div>
                      <p v-if="company.address" class="modal-list-item-address">
                        <font-awesome-icon icon="fa-solid fa-location-dot" class="text-gray-400 text-xs mr-1" />
                        {{ company.address }}
                      </p>
                    </div>
                    <div class="modal-list-item-arrow">
                      <font-awesome-icon icon="fa-solid fa-arrow-right" />
                    </div>
                  </button>
                </div>
              </div>

              <!-- Vendors Tab -->
              <div v-if="activeTab === 'vendors'">
                <!-- Search and Filters -->
                <div class="modal-search-container grid grid-cols-1 sm:grid-cols-12 gap-4">
                  <div class="modal-search-input-wrapper sm:col-span-5">
                    <font-awesome-icon icon="fa-solid fa-search" class="modal-search-icon" />
                    <input
                      v-model="vendorSearch"
                      type="text"
                      placeholder="Search vendors..."
                      class="form-input pl-12"
                      @input="filterVendors"
                    />
                  </div>
                  <div class="sm:col-span-4">
                    <select v-model="selectedCompanyId" @change="loadVendorsForCompany" class="form-input cursor-pointer">
                      <option :value="null">All Companies</option>
                      <option v-for="company in companies" :key="company.id" :value="company.id">
                        {{ company.name }}
                      </option>
                    </select>
                  </div>
                  <div class="sm:col-span-3">
                    <select v-model="vendorOrderBy" @change="filterVendors" class="form-input appearance-none cursor-pointer">
                      <option value="vendor_name">Alphabetical</option>
                      <option value="created_at">Recent</option>
                      <option value="vendor_vat">VAT</option>
                    </select>
                  </div>
                </div>

                <div v-if="loadingVendors" class="flex items-center justify-center py-16">
                  <div class="animate-spin rounded-full h-10 w-10 border-2 border-gray-200 border-t-blue-600"></div>
                </div>

                <div v-else-if="filteredVendors.length === 0" class="flex flex-col items-center justify-center py-16 text-center">
                  <div class="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                    <font-awesome-icon icon="fa-solid fa-users" class="text-gray-400 text-2xl" />
                  </div>
                  <p class="text-gray-900 font-medium text-lg">No vendors found</p>
                  <p class="text-base text-gray-500 mt-1">Try adjusting your search or filters</p>
                </div>

                <div v-else class="space-y-3">
                  <button
                    v-for="vendor in filteredVendors"
                    :key="vendor.id"
                    @click="selectVendor(vendor)"
                    class="modal-list-item"
                  >
                    <div class="modal-list-item-icon vendor">
                      <font-awesome-icon icon="fa-solid fa-user-tie" />
                    </div>
                    <div class="modal-list-item-content">
                      <div class="modal-list-item-header">
                        <h3 class="modal-list-item-title">{{ vendor.vendor_name }}</h3>
                        <span v-if="vendor.vendor_vat" class="modal-list-item-badge">
                          {{ vendor.vendor_vat }}
                        </span>
                        <span v-if="vendor.company" class="modal-list-item-badge company-badge">
                          <font-awesome-icon icon="fa-solid fa-building" class="text-xs mr-1" />
                          {{ vendor.company.name }}
                        </span>
                      </div>
                      <p v-if="vendor.vendor_address" class="modal-list-item-address">
                        <font-awesome-icon icon="fa-solid fa-location-dot" class="text-gray-400 text-xs mr-1" />
                        {{ vendor.vendor_address }}
                      </p>
                    </div>
                    <div class="modal-list-item-arrow">
                      <font-awesome-icon icon="fa-solid fa-arrow-right" />
                    </div>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </transition>
  </teleport>
</template>

<script setup>
import { ref, watch, computed } from 'vue';
import api from '@/utils/api';

const props = defineProps({
  show: {
    type: Boolean,
    required: true
  }
});

const emit = defineEmits(['close', 'select']);

const activeTab = ref('companies');
const companies = ref([]);
const vendors = ref([]);
const selectedCompanyId = ref(null);
const loadingCompanies = ref(false);
const loadingVendors = ref(false);

// Search and sort
const companySearch = ref('');
const companyOrderBy = ref('name');
const vendorSearch = ref('');
const vendorOrderBy = ref('vendor_name');

const tabs = [
  { id: 'companies', label: 'Companies' },
  { id: 'vendors', label: 'Vendors' }
];

// Filtered and sorted companies
const filteredCompanies = computed(() => {
  let filtered = companies.value;

  // Apply search filter
  if (companySearch.value) {
    const search = companySearch.value.toLowerCase();
    filtered = filtered.filter(company =>
      company.name?.toLowerCase().includes(search) ||
      company.vat_number?.toLowerCase().includes(search) ||
      company.address?.toLowerCase().includes(search)
    );
  }

  // Apply sorting
  return filtered.sort((a, b) => {
    if (companyOrderBy.value === 'name') {
      return (a.name || '').localeCompare(b.name || '');
    } else if (companyOrderBy.value === 'vat_number') {
      return (a.vat_number || '').localeCompare(b.vat_number || '');
    } else if (companyOrderBy.value === 'created_at') {
      return new Date(b.created_at) - new Date(a.created_at);
    }
    return 0;
  });
});

// Filtered and sorted vendors
const filteredVendors = computed(() => {
  let filtered = vendors.value;

  // Apply search filter
  if (vendorSearch.value) {
    const search = vendorSearch.value.toLowerCase();
    filtered = filtered.filter(vendor =>
      vendor.vendor_name?.toLowerCase().includes(search) ||
      vendor.vendor_vat?.toLowerCase().includes(search) ||
      vendor.vendor_address?.toLowerCase().includes(search)
    );
  }

  // Apply sorting
  return filtered.sort((a, b) => {
    if (vendorOrderBy.value === 'vendor_name') {
      return (a.vendor_name || '').localeCompare(b.vendor_name || '');
    } else if (vendorOrderBy.value === 'vendor_vat') {
      return (a.vendor_vat || '').localeCompare(b.vendor_vat || '');
    } else if (vendorOrderBy.value === 'created_at') {
      return new Date(b.created_at) - new Date(a.created_at);
    }
    return 0;
  });
});

const filterCompanies = () => {
  // Trigger reactivity - computed property will handle it
};

const filterVendors = () => {
  // Trigger reactivity - computed property will handle it
};

const closeModal = () => {
  emit('close');
};

const selectCompany = (company) => {
  emit('select', { type: 'company', data: company });
};

const selectVendor = (vendor) => {
  emit('select', { type: 'vendor', data: vendor });
};

const loadCompanies = async () => {
  loadingCompanies.value = true;
  try {
    const response = await api.get('/api/companies', {
      params: {
        order_by: companyOrderBy.value,
        order_direction: 'asc'
      }
    });
    companies.value = response.data?.data || response.data || [];
  } catch (error) {
    console.error('Failed to load companies:', error);
    companies.value = [];
  } finally {
    loadingCompanies.value = false;
  }
};

const loadVendorsForCompany = async () => {
  loadingVendors.value = true;
  try {
    if (selectedCompanyId.value) {
      // Load vendors for specific company
      const response = await api.get(`/api/companies/${selectedCompanyId.value}/vendors`, {
        params: {
          order_by: vendorOrderBy.value,
          order_direction: 'asc'
        }
      });
      vendors.value = response.data?.data || response.data || [];
    } else {
      // Load all vendors from all companies
      const allVendorsPromises = companies.value.map(company =>
        api.get(`/api/companies/${company.id}/vendors`, {
          params: {
            order_by: vendorOrderBy.value,
            order_direction: 'asc'
          }
        }).catch(() => ({ data: [] }))
      );
      const vendorsResponses = await Promise.all(allVendorsPromises);
      vendors.value = vendorsResponses.flatMap((r, index) => {
        const vendorsList = r.data?.data || r.data || [];
        // Add company reference to each vendor
        return vendorsList.map(v => ({
          ...v,
          company: companies.value[index]
        }));
      });
    }
  } catch (error) {
    console.error('Failed to load vendors:', error);
    vendors.value = [];
  } finally {
    loadingVendors.value = false;
  }
};

// Load data when modal opens
watch(() => props.show, (newVal) => {
  if (newVal) {
    loadCompanies();
    loadVendorsForCompany();
  }
});

// Reload vendors when company filter changes or tab changes
watch(activeTab, (newTab) => {
  if (newTab === 'vendors' && vendors.value.length === 0) {
    loadVendorsForCompany();
  }
});
</script>

<style scoped>
.modal-enter-active,
.modal-leave-active {
  transition: opacity 0.3s ease;
}

.modal-enter-from,
.modal-leave-to {
  opacity: 0;
}

.modal-enter-active .relative,
.modal-leave-active .relative {
  transition: transform 0.3s ease;
}

.modal-enter-from .relative,
.modal-leave-to .relative {
  transform: scale(0.9);
}
</style>